import { useState, useEffect, useCallback, useMemo } from 'react';

const PERMISOS_KEY = 'permisos';
const MODULOS_KEY = 'modulos';
const PERMISSIONS_UPDATED_EVENT = 'permissions:updated';

const readPermisos = () => {
  try {
    const raw = localStorage.getItem(PERMISOS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

// `modulos` se guarda como JSON.stringify(array), pero en el código existente
// se consulta con .includes(string) directo sobre el string. Mantenemos ese
// patrón para no romper compatibilidad (ver MenuContent.js).
const readModulos = () => localStorage.getItem(MODULOS_KEY) || '';

/**
 * Hook central para consultar los permisos del usuario logueado.
 *
 * Los permisos se guardan en localStorage tras el login (ver useSession.setSession)
 * con la forma { modulo: ['accion1', 'accion2', ...] }.
 *
 * Este hook se sincroniza automáticamente cuando:
 *  - Otra pestaña modifica localStorage (evento `storage` nativo)
 *  - La pestaña actual dispara `notifyPermissionsUpdated()` después de
 *    actualizar los permisos en localStorage.
 *
 * Ejemplos:
 *   const { hasPermission } = usePermissions();
 *   if (hasPermission('prestamos', 'crear')) { ... }
 *
 *   const { hasModule } = usePermissions();
 *   if (hasModule('inversionistas')) { ... }
 */
export default function usePermissions() {
  const [permisos, setPermisos] = useState(readPermisos);
  const [modulos, setModulos] = useState(readModulos);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === PERMISOS_KEY) setPermisos(readPermisos());
      if (e.key === MODULOS_KEY) setModulos(readModulos());
    };
    const onLocalUpdate = () => {
      setPermisos(readPermisos());
      setModulos(readModulos());
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener(PERMISSIONS_UPDATED_EVENT, onLocalUpdate);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(PERMISSIONS_UPDATED_EVENT, onLocalUpdate);
    };
  }, []);

  const hasPermission = useCallback(
    (modulo, accion) => {
      if (!modulo) return true;
      const acciones = permisos?.[modulo];
      if (!Array.isArray(acciones)) return false;
      if (!accion) return acciones.length > 0;
      return acciones.includes(accion);
    },
    [permisos]
  );

  const hasModule = useCallback(
    (modulo) => Boolean(modulo) && modulos.includes(modulo),
    [modulos]
  );

  const hasAny = useCallback(
    (checks) =>
      Array.isArray(checks) ? checks.some(([m, a]) => hasPermission(m, a)) : false,
    [hasPermission]
  );

  const hasAll = useCallback(
    (checks) =>
      Array.isArray(checks) ? checks.every(([m, a]) => hasPermission(m, a)) : true,
    [hasPermission]
  );

  return useMemo(
    () => ({ permisos, modulos, hasPermission, hasModule, hasAny, hasAll }),
    [permisos, modulos, hasPermission, hasModule, hasAny, hasAll]
  );
}

/**
 * Notifica a todos los `usePermissions` montados en la pestaña actual que los
 * permisos cambiaron. Llamar después de actualizar localStorage.permisos
 * (ej. tras editar el rol propio o tras hacer fetch a /api/auth/me).
 *
 * El evento `storage` nativo NO se dispara en la pestaña que hizo el cambio,
 * por eso necesitamos este evento custom.
 */
export const notifyPermissionsUpdated = () => {
  window.dispatchEvent(new Event(PERMISSIONS_UPDATED_EVENT));
};
